import type {MediaSessionLike} from '@pexip/media';
import {isMobileDevice} from '@pexip/media-components';

/**
 * Reason why picture in picture cannot be offered for the given element, or
 * `undefined` when it can.
 */
export const getPictureInPictureBlocker = (mediaElement: HTMLMediaElement) => {
    if (!document.pictureInPictureEnabled) {
        return 'The browser does not allow picture in picture';
    }
    if (!(mediaElement instanceof HTMLVideoElement)) {
        return 'The remote media is not played by a video element';
    }
    if (mediaElement.disablePictureInPicture) {
        return 'The video element opts out of picture in picture';
    }
    return undefined;
};

/**
 * The action is an IDL enum value, so a browser not implementing it rejects
 * the registration, which is the only way to detect it.
 */
const supportsEnterPictureInPictureAction = () => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
        return false;
    }
    const mediaSession: MediaSessionLike = navigator.mediaSession;
    try {
        mediaSession.setActionHandler('enterpictureinpicture', null);
        return true;
    } catch {
        return false;
    }
};

// Probed once on load, as clearing the handler later would drop the one
// registered for an ongoing meeting.
const autoPictureInPictureSupported =
    !isMobileDevice() &&
    typeof document !== 'undefined' &&
    Boolean(document.pictureInPictureEnabled) &&
    supportsEnterPictureInPictureAction();

/**
 * Whether the browser can automatically pop out the meeting video, e.g. when
 * the user switches to another tab.
 */
export const browserSupportsAutoPictureInPicture = () =>
    autoPictureInPictureSupported;
